import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtTime } from "@/lib/api";
import { PageHeader, StatusDot } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Building2, MonitorSmartphone, Users, LifeBuoy, KeyRound, Copy, Check } from "lucide-react";

const TICKET_STATUS = {
  OPEN: { label: "Offen", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  IN_PROGRESS: { label: "In Arbeit", cls: "text-blue-400 border-blue-400/40 bg-blue-400/10" },
  RESOLVED: { label: "Gelöst", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  CLOSED: { label: "Geschlossen", cls: "text-slate-400 border-slate-400/40 bg-slate-400/10" },
};

export default function TenantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [resettingId, setResettingId] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  async function resetPassword(u) {
    setResettingId(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetResult(data);
      setCopied(false);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setResettingId(null);
    }
  }

  async function copyCredentials() {
    const text = [
      "REX//SIGNAGE — Zugangsdaten",
      `Portal: ${window.location.origin}`,
      `E-Mail: ${resetResult.email}`,
      `Vorläufiges Passwort: ${resetResult.password}`,
      "Hinweis: Das Passwort muss beim ersten Login geändert werden.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
    }
  }

  const load = useCallback(() => {
    api.get(`/tenants/${id}/overview`).then((r) => setData(r.data)).catch((e) => setError(fmtErr(e)));
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div className="panel p-8 text-center" data-testid="tenant-detail-error">
        <p className="font-mono text-xs text-red-400">{error}</p>
        <Link to="/tenants" className="btn-ghost inline-flex mt-4 text-xs" data-testid="tenant-back-link">
          <ArrowLeft size={13} /> Zurück zu Mandanten
        </Link>
      </div>
    );
  }

  if (!data) {
    return <div className="font-mono text-xs text-cyan-400 animate-pulse" data-testid="tenant-detail-loading">Lade Kunden-Akte…</div>;
  }

  const { tenant, users, devices, tickets } = data;
  const online = devices.filter((d) => d.online).length;
  const openTickets = tickets.filter((t) => t.status === "OPEN" || t.status === "IN_PROGRESS").length;

  return (
    <div data-testid="tenant-detail-page">
      <PageHeader
        title={tenant.name}
        sub={`Kunden-Akte · angelegt ${fmtTime(tenant.created_at)}`}
      >
        <Link to="/tenants" className="btn-ghost text-xs" data-testid="tenant-back-link">
          <ArrowLeft size={13} /> Alle Mandanten
        </Link>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tenant-detail-stats">
        <div className="panel p-4">
          <div className="mono-label flex items-center gap-1.5 mb-1"><MonitorSmartphone size={11} /> Geräte</div>
          <div className="font-display text-2xl font-bold text-slate-100" data-testid="tenant-stat-devices">{devices.length}</div>
          <div className="font-mono text-[10px] text-emerald-400 mt-0.5">{online} online</div>
        </div>
        <div className="panel p-4">
          <div className="mono-label flex items-center gap-1.5 mb-1"><Users size={11} /> Benutzer</div>
          <div className="font-display text-2xl font-bold text-slate-100" data-testid="tenant-stat-users">{users.length}</div>
        </div>
        <div className="panel p-4">
          <div className="mono-label flex items-center gap-1.5 mb-1"><LifeBuoy size={11} /> Tickets</div>
          <div className="font-display text-2xl font-bold text-slate-100" data-testid="tenant-stat-tickets">{tickets.length}</div>
          <div className="font-mono text-[10px] text-amber-400 mt-0.5">{openTickets} offen</div>
        </div>
        <div className="panel p-4">
          <div className="mono-label flex items-center gap-1.5 mb-1"><Building2 size={11} /> Status</div>
          <div className="mt-1" data-testid="tenant-stat-status">
            {tenant.subscription_status === "CANCELLED" ? (
              <span className="font-mono text-[10px] uppercase tracking-widest text-red-400 border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 rounded-sm">Gekündigt</span>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 rounded-sm">Aktiv</span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="panel" data-testid="tenant-detail-devices">
          <div className="mono-label px-5 pt-4 pb-3 border-b border-border/60">Geräte des Mandanten</div>
          {devices.length === 0 ? (
            <div className="px-5 py-6 font-mono text-xs text-slate-400" data-testid="tenant-devices-empty">Keine Geräte gekoppelt</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="mono-label font-normal px-5 py-2.5">Display</th>
                  <th className="mono-label font-normal px-3 py-2.5">Status</th>
                  <th className="mono-label font-normal px-3 py-2.5">Standort</th>
                  <th className="mono-label font-normal px-3 py-2.5">Playlist</th>
                  <th className="mono-label font-normal px-3 py-2.5">Gruppe</th>
                  <th className="mono-label font-normal px-3 py-2.5">Letzter Kontakt</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-b border-border/50 hover:bg-slate-800/40 transition-colors" data-testid={`tenant-device-row-${d.id.slice(0, 8)}`}>
                    <td className="px-5 py-2.5">
                      <Link to={`/devices/${d.id}`} className="text-cyan-300 hover:text-cyan-200 text-xs font-medium" data-testid={`tenant-device-link-${d.id.slice(0, 8)}`}>
                        {d.display_name || d.label || "Unbenannt"}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5"><StatusDot online={d.online} status={d.status} /></td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{d.location_name || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{d.playlist_name || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{d.group_name || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-slate-400">{fmtTime(d.state?.last_seen_at || d.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="panel" data-testid="tenant-detail-users">
            <div className="mono-label px-5 pt-4 pb-3 border-b border-border/60">Benutzer</div>
            {users.length === 0 ? (
              <div className="px-5 py-6 font-mono text-xs text-slate-400">Keine Benutzer</div>
            ) : (
              <div className="divide-y divide-border/50">
                {users.map((u, i) => (
                  <div key={u.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`tenant-user-row-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-200 truncate">{u.name || u.email}</div>
                      <div className="font-mono text-[10px] text-slate-400 truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => resetPassword(u)}
                        disabled={resettingId === u.id}
                        className="p-1.5 rounded-sm text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors disabled:opacity-40"
                        title="Passwort zurücksetzen — neues vorläufiges Passwort wird einmalig angezeigt"
                        data-testid={`tenant-user-reset-${i}`}
                      >
                        <KeyRound size={14} />
                      </button>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-400/80">{u.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel" data-testid="tenant-detail-tickets">
            <div className="mono-label px-5 pt-4 pb-3 border-b border-border/60 flex items-center justify-between">
              <span>Support-Tickets</span>
              <Link to="/support" className="text-cyan-400 hover:text-cyan-300 normal-case tracking-normal text-[11px]" data-testid="tenant-tickets-link">
                Zum Support →
              </Link>
            </div>
            {tickets.length === 0 ? (
              <div className="px-5 py-6 font-mono text-xs text-slate-400" data-testid="tenant-tickets-empty">Keine Tickets</div>
            ) : (
              <div className="divide-y divide-border/50">
                {tickets.map((t, i) => {
                  const meta = TICKET_STATUS[t.status] || TICKET_STATUS.OPEN;
                  return (
                    <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`tenant-ticket-row-${i}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-200 truncate">{t.subject}</div>
                        <div className="font-mono text-[10px] text-slate-400">{fmtTime(t.updated_at)}</div>
                      </div>
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border shrink-0 ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!resetResult} onOpenChange={(o) => { if (!o) setResetResult(null); }}>
        <DialogContent className="bg-card border-border" data-testid="reset-credentials-dialog">
          <DialogTitle className="font-display flex items-center gap-2 text-cyan-300">
            <KeyRound size={16} /> Neues Passwort gesetzt
          </DialogTitle>
          {resetResult && (
            <div className="space-y-4">
              <p className="text-xs text-slate-300 leading-relaxed">
                Für <span className="font-semibold text-slate-100">{resetResult.email}</span> wurde ein
                vorläufiges Passwort gesetzt. Es wird <span className="text-amber-300">nur jetzt einmalig</span> angezeigt —
                der Kunde muss es beim nächsten Login ändern.
              </p>
              <div className="panel !bg-slate-900/60 p-4 font-mono text-xs space-y-1.5" data-testid="reset-credentials-box">
                <div><span className="text-slate-400">Portal:</span> <span className="text-slate-200">{window.location.origin}</span></div>
                <div><span className="text-slate-400">E-Mail:</span> <span className="text-slate-200">{resetResult.email}</span></div>
                <div><span className="text-slate-400">Passwort:</span> <span className="text-cyan-300 select-all">{resetResult.password}</span></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setResetResult(null)} className="btn-ghost flex-1 text-xs" data-testid="reset-close">
                  Schließen
                </button>
                <button
                  onClick={copyCredentials}
                  className="btn-primary flex-1 !py-2 text-xs inline-flex items-center justify-center gap-1.5"
                  data-testid="reset-copy-button"
                >
                  {copied ? <><Check size={13} /> Kopiert!</> : <><Copy size={13} /> Zugangsdaten kopieren</>}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
